import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { XMarkIcon, ChevronDownIcon, ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { MdContrast, MdFormatSize, MdMap } from "react-icons/md";
import { useAccessibility } from "../../context/AccessibilityContext";

const FONT_SIZES = [
  { key: "small", label: "A−", aria: "Texto pequeno" },
  { key: "normal", label: "A", aria: "Texto normal" },
  { key: "large", label: "A+", aria: "Texto grande" },
  { key: "xlarge", label: "A++", aria: "Texto extra grande" },
];

const FEATURES = [
  { key: "highContrast", name: "Alto Contraste", Icon: MdContrast },
  { key: "fontSize", name: "Tamanho do Texto", Icon: MdFormatSize },
  { key: "siteMap", name: "Mapa do Site", Icon: MdMap },
];

export default function AccessibilitySidebar() {
  const { settings, updateSetting, resetSettings, sidebarOpen, setSidebarOpen } =
    useAccessibility();
  const [expanded, setExpanded] = useState(null);

  const toggle = (key) => setExpanded((prev) => (prev === key ? null : key));

  useEffect(() => {
    if (!sidebarOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setSidebarOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [sidebarOpen, setSidebarOpen]);

  if (!sidebarOpen) return null;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Configurações de acessibilidade">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar panel */}
      <aside className="absolute inset-y-0 right-0 w-80 bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-blue-950 flex-shrink-0">
          <h2 className="text-white font-bold text-lg tracking-wide">Acessibilidade</h2>
          <button
            onClick={() => setSidebarOpen(false)}
            className="text-white/70 hover:text-white transition-colors p-1 rounded-md hover:bg-white/10"
            aria-label="Fechar painel de acessibilidade"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Feature list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          <p className="text-xs text-gray-400 uppercase font-semibold tracking-wider mb-3">
            Configurações
          </p>

          {FEATURES.map(({ key, name, Icon }) => (
            <div key={key}>
              <button
                onClick={() => toggle(key)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left font-medium transition-colors ${
                  expanded === key
                    ? "bg-blue-600 text-white shadow-sm"
                    : "bg-gray-100 text-gray-700 hover:bg-blue-50 hover:text-blue-800"
                }`}
              >
                <Icon className="text-xl flex-shrink-0" aria-hidden="true" />
                <span className="flex-1 text-sm">{name}</span>
                <ChevronDownIcon
                  className={`h-4 w-4 transition-transform duration-200 ${
                    expanded === key ? "rotate-180" : ""
                  }`}
                  aria-hidden="true"
                />
              </button>

              {expanded === key && (
                <div className="mt-1 mx-1 p-4 bg-gray-50 border border-gray-200 rounded-xl">
                  {key === "highContrast" && (
                    <HighContrastConfig settings={settings} updateSetting={updateSetting} />
                  )}
                  {key === "fontSize" && (
                    <FontSizeConfig settings={settings} updateSetting={updateSetting} />
                  )}
                  {key === "siteMap" && (
                    <SiteMapConfig onClose={() => setSidebarOpen(false)} />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t flex-shrink-0">
          <button
            onClick={resetSettings}
            className="w-full py-2 text-sm text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            Restaurar configurações padrão
          </button>
        </div>
      </aside>
    </div>
  );
}

function HighContrastConfig({ settings, updateSetting }) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500 leading-relaxed">
        Aumenta o contraste visual para facilitar a leitura em telas.
      </p>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">
          {settings.highContrast ? "Ativado" : "Desativado"}
        </span>
        <button
          onClick={() => updateSetting("highContrast", !settings.highContrast)}
          role="switch"
          aria-checked={settings.highContrast}
          aria-label="Alternar alto contraste"
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
            settings.highContrast ? "bg-blue-600" : "bg-gray-300"
          }`}
        >
          <span
            className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${
              settings.highContrast ? "translate-x-5" : "translate-x-0.5"
            }`}
          />
        </button>
      </div>
    </div>
  );
}

function FontSizeConfig({ settings, updateSetting }) {
  const current = FONT_SIZES.find((s) => s.key === settings.fontSize);
  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500 leading-relaxed">
        Ajuste o tamanho do texto em todo o portal.
      </p>
      <div className="grid grid-cols-4 gap-2">
        {FONT_SIZES.map(({ key, label, aria }) => (
          <button
            key={key}
            onClick={() => updateSetting("fontSize", key)}
            aria-label={aria}
            aria-pressed={settings.fontSize === key}
            className={`py-2 rounded-lg font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              settings.fontSize === key
                ? "bg-blue-600 text-white shadow-sm"
                : "bg-gray-200 text-gray-700 hover:bg-blue-100 hover:text-blue-800"
            }`}
            style={{ fontSize: key === "small" ? 11 : key === "large" ? 15 : key === "xlarge" ? 17 : 13 }}
          >
            {label}
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-400 text-center">{current?.aria}</p>
    </div>
  );
}

function SiteMapConfig({ onClose }) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500 leading-relaxed">
        Veja todas as páginas e seções do portal de forma organizada.
      </p>
      <Link
        to="/transparencia/acessibilidade/mapa-do-site"
        onClick={onClose}
        className="flex items-center justify-center gap-2 w-full py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        Ver Mapa do Site
        <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
      </Link>
    </div>
  );
}
